/**
 * Rings and icon tiles (design v2.1), first built for the learner space and shared here:
 * concentric activity rings (Apple Fitness-like), a single ring with a value in the middle, and a
 * tinted icon tile. Tones only come from the tokens (ui.css `.ao-tone--*`); never colour alone:
 * the values and names are always written next to them.
 */

import type { CSSProperties, ReactNode } from "react";

/** Colour of a ring or a tile, mapped onto the status and accent tokens. */
export type Tone = "accent" | "success" | "warning" | "danger" | "info" | "red" | "neutral";

/** Tinted tile holding an icon (skills, sections, figures). Decorative: the name is written next to it. */
export function SkillIcon({
  icon,
  tone = "accent",
  size = "md",
  className = "",
}: {
  /** A lucide icon (size 18, strokeWidth 1.75–1.9). */
  icon: ReactNode;
  tone?: Tone;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  return (
    <span className={`ao-icon-tile ao-icon-tile--${size} ao-tone--${tone} ${className}`.trim()} aria-hidden="true">
      {icon}
    </span>
  );
}

export interface RingSpec {
  key: string;
  tone: Tone;
  /** 0 … 1 (1 = the goal is reached). */
  ratio: number;
}

/**
 * Concentric activity rings, outermost first. Decorative on their own: write the values next to
 * them (legend) or give a `label`.
 */
export function ActivityRings({
  rings,
  size = 200,
  stroke = 16,
  label,
  children,
  className = "",
}: {
  rings: readonly RingSpec[];
  size?: number;
  stroke?: number;
  label?: string;
  children?: ReactNode;
  className?: string;
}) {
  const gap = Math.max(2, Math.round(stroke * 0.18));
  const center = size / 2;
  return (
    <div
      className={`ao-rings ${className}`.trim()}
      style={{ width: size, height: size }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        {rings.map((ring, index) => {
          const radius = center - stroke / 2 - index * (stroke + gap);
          if (radius <= stroke / 2) return null;
          const circumference = 2 * Math.PI * radius;
          const ratio = Math.max(0, Math.min(1, ring.ratio));
          return (
            <g key={ring.key} className={`ao-activity ao-tone--${ring.tone}`} style={{ "--ao-i": index } as CSSProperties}>
              <circle className="ao-activity__track" cx={center} cy={center} r={radius} strokeWidth={stroke} />
              <circle
                className="ao-activity__arc"
                cx={center}
                cy={center}
                r={radius}
                strokeWidth={stroke}
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - ratio)}
                transform={`rotate(-90 ${center} ${center})`}
              />
            </g>
          );
        })}
      </svg>
      {children ? <div className="ao-rings__center">{children}</div> : null}
    </div>
  );
}

/** One ring with a value in the middle (scores, challenges, countdowns). */
export function Ring({
  ratio,
  size = 120,
  stroke = 10,
  tone = "accent",
  children,
  className = "",
}: {
  ratio: number;
  size?: number;
  stroke?: number;
  tone?: Tone;
  children?: ReactNode;
  className?: string;
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = Math.max(0, Math.min(1, ratio));
  return (
    <div className={`ao-ring-one ao-tone--${tone} ${className}`.trim()} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        <circle className="ao-activity__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} />
        <circle
          className="ao-activity__arc"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="ao-rings__center">{children}</div>
    </div>
  );
}
